import { Box, Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import FormTable, {
  DataItem,
} from "../../../../../common/components/functional/formTable/FormTable";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import ZonedSalaryValue from "../../../../../common/components/functional/ZonedSalaryValue";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import {
  calculateTotalSalary,
  calculateZonedTotalSalary,
} from "../../../../../common/utils/salaryUtils";
import { useConfirmationDialog } from "../../../../../contexts/ConfirmationDialogContext";
import {
  fetchEmploymentAttributesForEditing,
  updateTargetedShiftSpec,
} from "../../../../employment/employmentSlice";
import {
  buildSalaryKey,
  getActiveSalaryRange,
  hasMultipleDateRanges,
  toCommandKeys,
} from "../../../../employment/targetedShiftSalaries/helpers";
import { removeTargetedShiftSalaryRow } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesActions";
import { TargetedShiftSpec } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";

import TargetedShiftSalaryDialog from "../../../../employment/targetedShiftSalaries/TargetedShiftSalaryDialog";
import { TargetedShiftSalaryDialogState } from "../../../../employment/targetedShiftSalaries/types";
import { ContentKey } from "../../../TalentDetails";

const EMPTY_SPEC: TargetedShiftSpec = {
  version: 0,
  targetingRules: [],
  salaries: [],
  variationValidationAssignmentId: null,
};

interface Props {
  employmentId: string;
  tesId: string;
  talentId: string;
  workTitleId: string;
  canEdit: boolean;
  noBorder?: boolean;
  initialSpec?: TargetedShiftSpec;
  hasTargetingRules?: boolean;
  contractId?: string;
  employmentStart?: string;
  employmentEnd?: string;
}

export const TargetedShiftSalaries = ({
  employmentId,
  tesId,
  talentId,
  workTitleId,
  canEdit,
  noBorder = false,
  initialSpec,
  hasTargetingRules,
  contractId,
  employmentStart,
  employmentEnd,
}: Props) => {
  const { t } = useTranslation(["talentProfile", "common"]);
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmationDialog();
  const reduxVersion = useAppSelector(
    (s) => s.employment.additionalInfo?.targetedShiftSpec?.version ?? 0,
  );

  const [spec, setSpec] = useState<TargetedShiftSpec>(
    initialSpec ?? EMPTY_SPEC,
  );
  const [dialogState, setDialogState] =
    useState<TargetedShiftSalaryDialogState>(null);

  const customerCompany = spec.targetingRules[0]?.custCompany;

  const workTitleTranslations = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const employmentAttributesForEditing = useAppSelector(
    (state) => state.employment.attributesForEditing,
  );

  useEffect(() => {
    if (talentId && workTitleId && tesId) {
      dispatch(
        fetchEmploymentAttributesForEditing({ talentId, workTitleId, tesId }),
      );
    }
  }, [talentId, workTitleId, tesId, dispatch]);

  const salaryZones =
    employmentAttributesForEditing.attributes?.tesId === tesId
      ? employmentAttributesForEditing.attributes?.salaryZones
      : undefined;

  useEffect(() => {
    if (initialSpec) {
      setSpec(initialSpec);
    }
  }, [initialSpec]);

  const getWorkTitleNames = (ids: string[]): string => {
    if (!ids.length) return t("talentProfile:allWorkTitles");
    return ids.map((id) => workTitleTranslations?.[id] ?? id).join(", ");
  };

  const today = new Date();

  const tableData: DataItem[] = spec.salaries.map((row) => {
    const keys = row.keys;
    const salary = row.salary;
    const activeRange =
      getActiveSalaryRange(salary, today) ?? salary.ranges.at(0);
    const multipleRanges = hasMultipleDateRanges(salary);

    const dateDisplay = activeRange ? (
      <Box display="flex" alignItems="center" gap={0.5} component="span">
        {format(new Date(activeRange.from), leadingZerosDateFormat)}
        {multipleRanges && (
          <InfoTooltip
            title={t("talentProfile:multipleSalariesIndicatorTooltip")}
            variant="warning"
            placement="top"
          />
        )}
      </Box>
    ) : (
      "-"
    );

    const totalSalary = (
      <ZonedSalaryValue
        defaultValue={
          activeRange?.components
            ? calculateTotalSalary(activeRange.components)
            : "-"
        }
        zones={salaryZones}
        zoneValues={calculateZonedTotalSalary(activeRange?.zonedComponents)}
      />
    );

    return {
      values: {
        date: dateDisplay,
        organisation: keys.custOrgs.length
          ? keys.custOrgs.map((o) => o.name || o.id).join(", ")
          : t("talentProfile:allOrganizations"),
        workLocation: keys.workLocs.length
          ? keys.workLocs.map((wl) => wl.name || wl.id).join(", ")
          : t("talentProfile:allWorkLocations"),
        workTitle: getWorkTitleNames(keys.workTitles),
        totalSalary,
      },
      id: buildSalaryKey(keys),
      deletable: canEdit,
    };
  });

  const headers = [
    { text: t("talentProfile:date") },
    { text: t("talentProfile:organization") },
    { text: t("common:workLocation") },
    { text: t("common:workTitle") },
    { text: t("talentProfile:totalSalary") },
  ];

  const handleOpenEditModal = (_: ContentKey, id?: string) => {
    if (id) {
      const row = spec.salaries.find((r) => buildSalaryKey(r.keys) === id);
      if (row) setDialogState({ mode: "edit", editingKey: row.keys });
    } else {
      setDialogState({ mode: "add" });
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t("common:confirmDelete"),
      contentText: t("talentProfile:confirmDeletingTargetedShiftSalary"),
      confirmText: t("common:delete"),
      cancelText: t("common:cancel"),
    });
    if (!confirmed) return;

    const row = spec.salaries.find((r) => buildSalaryKey(r.keys) === id);
    if (!row) return;

    const action = await dispatch(
      removeTargetedShiftSalaryRow({
        employmentId,
        version: Math.max(reduxVersion, spec.version),
        keys: toCommandKeys(row.keys),
      }),
    );
    if (isFulfilled(action)) {
      const newVersion = action.payload[0]?.version ?? spec.version;
      const updatedSalaries = spec.salaries.filter(
        (r) => buildSalaryKey(r.keys) !== id,
      );
      const updatedSpec = {
        ...spec,
        version: newVersion,
        salaries: updatedSalaries,
      };
      setSpec(updatedSpec);
      dispatch(updateTargetedShiftSpec(updatedSpec));
    }
  };

  return (
    <FormSection
      title={t("talentProfile:targetedShiftSalaries")}
      margin
      noBorder={noBorder}
      disablePaddingBottom
    >
      <Grid item xs={12}>
        <FormTable
          data={tableData}
          headers={headers}
          contentKey=""
          handleOpenEditModal={handleOpenEditModal}
          handleDelete={canEdit ? handleDelete : undefined}
          addButtonLabel={
            canEdit && (hasTargetingRules ?? spec.targetingRules.length > 0)
              ? t("talentProfile:addTargetedShiftSalary")
              : null
          }
          disableEditRow={!canEdit}
        />
      </Grid>

      <TargetedShiftSalaryDialog
        mode="employment"
        open={dialogState !== null}
        employmentId={employmentId}
        tesId={tesId}
        talentId={talentId}
        workTitleId={workTitleId}
        spec={{ ...spec, version: Math.max(reduxVersion, spec.version) }}
        canEdit={canEdit}
        contractId={contractId}
        customerCompany={customerCompany}
        minDate={employmentStart ? parseISO(employmentStart) : null}
        maxDate={employmentEnd ? parseISO(employmentEnd) : null}
        onClose={() => setDialogState(null)}
        onSaved={(updated) => {
          setSpec(updated);
          dispatch(updateTargetedShiftSpec(updated));
          setDialogState(null);
        }}
        editingKey={
          dialogState?.mode === "edit" ? dialogState.editingKey : null
        }
      />
    </FormSection>
  );
};

export default TargetedShiftSalaries;
